import classNames from 'classnames';
import * as _ from 'lodash';

import { setupForDark } from '@theme';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InputErrorMessage from './InputErrorMessage';

const secondsToTime = (timeInSeconds: number) => {
  return {
    hours: Math.floor(timeInSeconds / 3600),
    minutes: Math.floor((timeInSeconds % 3600) / 60),
    seconds: timeInSeconds % 60
  };
};

const timeToSeconds = (time: { hours: number; minutes: number; seconds: number }) => {
  return time.hours * 3600 + time.minutes * 60 + time.seconds;
};

const formatTime = (type: string, time: { hours: number; minutes: number; seconds: number }) => {
  if (type === 'HOUR_MINUTE') {
    if (time.hours === 0) {
      return `${time.minutes}m`;
    }

    if (time.minutes === 0) {
      return `${time.hours}h`;
    }

    return `${time.hours}h ${time.minutes}m`;
  } else {
    if (time.minutes === 0) {
      return `${time.seconds}s`;
    }

    if (time.seconds === 0) {
      return `${time.minutes}m`;
    }

    return `${time.minutes}m ${time.seconds}s`;
  }
};

const formatSeconds = (type: string, timeInSeconds: number) => {
  return formatTime(type, secondsToTime(timeInSeconds));
};

function TimeInput({
  displayMode = 'default',
  type = 'MINUTE_SECOND',
  value,
  minValue,
  maxValue,
  onChange,
  onError
}: {
  displayMode?: 'default' | 'minimalistic';
  type?: 'HOUR_MINUTE' | 'MINUTE_SECOND';
  value: number;
  minValue?: number;
  maxValue?: number;
  onChange: (value: number) => void;
  onError?: (error: string) => void;
}) {
  const maxHours = maxValue / 3600;
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState({
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  const [error, setError] = useState<string | null>(null);
  const onErrorMessage = (error: string) => {
    if (onError) {
      onError(error);
    }
    setError(error);
  };

  const validate = (time: { hours: number; minutes: number; seconds: number }): boolean => {
    // HOURS
    if (_.isNaN(time.hours)) {
      onErrorMessage(t('components.timeInput.errorWrongHoursValue', { min: 0, max: maxHours }));
      return false;
    }

    // MINUTES
    if (_.isNaN(time.minutes)) {
      onErrorMessage(t('components.timeInput.errorWrongMinutesValue', { min: 0, max: 59 }));
      return false;
    }

    // SECONDS
    if (_.isNaN(time.seconds)) {
      onErrorMessage(t('components.timeInput.errorWrongSecondsValue', { min: 0, max: 59 }));
      return false;
    }

    const timeInSeconds = timeToSeconds(time);
    if (timeInSeconds < minValue || timeInSeconds > maxValue) {
      onErrorMessage(
        t('components.timeInput.errorWrongValueNoMinMax', {
          min: formatSeconds(type, minValue),
          max: formatSeconds(type, maxValue)
        })
      );
      return false;
    }

    if (!time || (time.hours === 0 && time.minutes === 0 && time.seconds === 0)) {
      onErrorMessage(t('components.timeInput.errorWrongValue'));
      return false;
    }

    onErrorMessage(null);
    return true;
  };

  useEffect(() => {
    setInputValue(secondsToTime(value));
  }, [value]);

  // check for errors only if the minValue, maxValue or inputValue changes
  useEffect(() => {
    validate(inputValue);
  }, [minValue, maxValue, inputValue]);

  const onHoursChange = (event) => {
    let hours = parseInt(event.target.value);

    setInputValue((prev) => ({
      ...prev,
      hours: hours
    }));

    if (
      validate({
        hours: hours,
        minutes: inputValue.minutes,
        seconds: inputValue.seconds
      })
    ) {
      onChange(
        timeToSeconds({
          hours: hours,
          minutes: inputValue.minutes,
          seconds: inputValue.seconds
        })
      );
    }
  };

  const onMinutesChange = (event) => {
    let minutes = parseInt(event.target.value);
    if (!_.isNaN(minutes)) {
      minutes = Math.min(minutes, 59);
    }

    setInputValue((prev) => ({
      ...prev,
      minutes: minutes
    }));

    if (
      validate({
        hours: inputValue.hours,
        minutes: minutes,
        seconds: inputValue.seconds
      })
    ) {
      onChange(
        timeToSeconds({
          hours: inputValue.hours,
          minutes: minutes,
          seconds: inputValue.seconds
        })
      );
    }
  };

  const onSecondsChange = (event) => {
    let seconds = parseInt(event.target.value);
    if (!_.isNaN(seconds)) {
      seconds = Math.min(seconds, 59);
    }

    setInputValue((prev) => ({
      ...prev,
      seconds: seconds
    }));

    if (
      validate({
        hours: inputValue.hours,
        minutes: inputValue.minutes,
        seconds: seconds
      })
    ) {
      onChange(
        timeToSeconds({
          hours: inputValue.hours,
          minutes: inputValue.minutes,
          seconds: seconds
        })
      );
    }
  };

  const onKeyDown = (e) => {
    let keyCode = e.keyCode || e.which;
    let keyValue = String.fromCharCode(keyCode);

    if (
      e.key === 'Backspace' ||
      keyCode === 8 ||
      e.key === 'Delete' ||
      keyCode === 46 ||
      e.key === 'Tab' ||
      keyCode === 9 ||
      e.key === 'ArrowLeft' ||
      keyCode === 37 ||
      e.key === 'ArrowUp' ||
      keyCode === 38 ||
      e.key === 'ArrowRight' ||
      keyCode === 39 ||
      e.key === 'ArrowDown' ||
      keyCode === 40
    ) {
      return;
    }

    if (!/\d/.test(keyValue)) {
      e.preventDefault();
    }
  };

  const onFocus = (e) => {
    e.target.select();
    setTimeout(() => {
      e.target.scrollIntoView();
    }, 500);
  };

  const inputClasses = classNames(
    'bg-transparent border-none outline-none hide-input-arrows text-right pr-[2px] w-[26px]'
  );

  const minimalisticClasses = classNames(
    'px-xs rounded-xxs',
    setupForDark('bg-variable-dropdown'),
    setupForDark('text-black'),
    'bg-opacity-15',
    'I2R'
  );

  const defaultClasses = classNames(
    'rounded-xs px-sm py-xs',
    setupForDark('bg-text-input-default'),
    setupForDark('border-text-input-default'),
    setupForDark('text-primary'),
    'border-[1px]',
    'P2M'
  );

  return (
    <div>
      <div
        className={classNames(
          displayMode === 'minimalistic' ? minimalisticClasses : defaultClasses,
          'w-fit',
          'flex flex-row items-center justify-start',
          error && onError ? setupForDark('border-error') : '',
          error && onError ? 'border-[1px]' : ''
        )}
      >
        {type === 'HOUR_MINUTE' && (
          <>
            <input
              name='hours'
              type='number'
              max={maxHours}
              min='0'
              step='1'
              value={inputValue.hours}
              onChange={onHoursChange}
              onKeyDown={onKeyDown}
              onFocus={onFocus}
              className={inputClasses}
            />
            <div>{t('shared.hoursShort')}</div>
          </>
        )}
        <input
          name='minutes'
          type='number'
          max='59'
          min='0'
          step='1'
          value={inputValue.minutes}
          onChange={onMinutesChange}
          onKeyDown={onKeyDown}
          onFocus={onFocus}
          className={inputClasses}
        />
        <div>{t('shared.minutesShort')}</div>
        {type === 'MINUTE_SECOND' && (
          <>
            <input
              name='seconds'
              type='number'
              max='59'
              min='0'
              step='1'
              value={inputValue.seconds}
              onChange={onSecondsChange}
              onKeyDown={onKeyDown}
              onFocus={onFocus}
              className={inputClasses}
            />
            <div>{t('shared.secondsShort')}</div>
          </>
        )}
      </div>
      {error && !onError && <InputErrorMessage error={error} />}
    </div>
  );
}

export default TimeInput;
